import React, { useState, useEffect, useCallback, useRef } from "react";
import { getImageUrl } from "helpers/Image.helper";
import { formatTimeAgo, formattedDateTimeForLocale, formattedTimeForLocale } from "helpers/Datetime.helper";
import { saveDiaryViewToLocalStorage } from "helpers/LocalStorage.helper";
import VideoPlayer from "commons/video/VideoPlayer";
import { usePopup } from "contexts/PopupContext";
import ImageSlidePopup from "pages/common/v2/ImageSlidePopup";
import { IDiary } from "api/interfaces/profile.interface";
import { getDiaryList, IDiaryListResult } from "api/agency/v2/diary.api";
import { toggleDiaryLike } from "api/agency/v2/diary.api";
import StudentProfilePopup from "../popup/StudentProfilePopup";
import StudentDiaryDetailPopup from "../popup/StudentDiaryDetailPopup";
import { getStudentNickname } from "helpers/String.helper";

interface DiaryListProps {
    userStudentId: number;
}

// 다이어리 아이템 컴포넌트 (2초 노출 감지 포함)
const DiaryItem: React.FC<{
    diary: IDiary;
    isLiking: boolean;
    onLikeToggle: (diary: IDiary, e: React.MouseEvent) => void;
    onCommentClick: (diaryId: number, e: React.MouseEvent) => void;
    onStudentInfoPopup: (userStudentId: number) => void;
    onImageClick: (images: any[], initialIndex: number) => void;
    onDiaryClick: (diaryId: number) => void;
    onRefresh: () => void;
}> = ({ diary, isLiking, onLikeToggle, onCommentClick, onStudentInfoPopup, onImageClick, onDiaryClick, onRefresh }) => {
    const { showPopup } = usePopup();
    const itemRef = useRef<HTMLDivElement>(null);
    const [hasBeenVisible, setHasBeenVisible] = useState(false);

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !hasBeenVisible) {
                        // 화면에 노출되기 시작 - 타이머 시작
                        timer = setTimeout(() => {
                            console.log("📊 다이어리 2초 노출 감지:", {
                                diaryId: diary.id,
                                title: diary.title,
                                studentName: diary.user?.userStudent?.nickname || diary.user?.username,
                                timestamp: new Date().toISOString()
                            });

                            // localStorage에 노출된 다이어리 저장
                            saveDiaryViewToLocalStorage(diary.id);

                            setHasBeenVisible(true);
                        }, 2000);
                    } else if (!entry.isIntersecting && timer) {
                        // 화면에서 벗어남 - 타이머 취소
                        clearTimeout(timer);
                        timer = null;
                    }
                });
            },
            { threshold: 0.5 } // 50% 이상 노출되었을 때 감지
        );

        if (itemRef.current) {
            observer.observe(itemRef.current);
        }

        return () => {
            if (itemRef.current) {
                observer.unobserve(itemRef.current);
            }
            // 타이머 정리
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [diary.id, diary.title, diary.user?.userStudent?.nickname, diary.user?.username, hasBeenVisible]);

    return (
        <div
            ref={itemRef}
            className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer"
            onClick={() => onDiaryClick(diary.id)}
        >
            {/* 다이어리 헤더 */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden">
                        <img
                            src={getImageUrl(diary.user?.userStudent?.profileImage, 80, 80)}
                            alt="Student Profile"
                            className="w-full h-full object-cover"
                            onClick={(e) => {
                                e.stopPropagation();
                                onStudentInfoPopup(diary.user?.userStudent?.id);
                            }}
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = "/imgs/dummy/basic-img.png";
                            }}
                        />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-800 text-sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                onStudentInfoPopup(diary.user?.userStudent?.id);
                            }}
                        >
                            {getStudentNickname(diary.user?.userStudent, '')}
                        </h3>
                        <p className="text-xs text-gray-400">
                            {formatTimeAgo(diary.createdAt)}
                        </p>
                    </div>
                </div>

                {/* 포트폴리오 배지 */}
                {diary.isPortfolio && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-[#CF3688]/10 text-[#CF3688] rounded-full text-xs font-medium">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        포트폴리오
                    </div>
                )}
            </div>

            {/* 다이어리 제목 */}
            <h4 className="font-bold text-gray-900 text-sm mb-1">
                {diary.title}
            </h4>

            {/* 다이어리 내용 */}
            <p className="text-gray-600 text-xs mb-2 line-clamp-2 leading-relaxed">
                {diary.content}
            </p>

            {/* 첨부된 비디오 (가장 먼저 출력) */}
            {diary.diaryVideos && diary.diaryVideos.length > 0 && (
                <div className="mb-2">
                    {diary.diaryVideos.map((diaryVideo, index) => (
                        <VideoPlayer
                            key={index}
                            video={diaryVideo.video}
                            className="w-full rounded-lg"
                        />
                    ))}
                </div>
            )}

            {/* 첨부된 이미지 */}
            {diary.diaryImages && diary.diaryImages.length > 0 && (
                <div className="mb-2">
                    <div className="flex gap-1 overflow-x-auto pb-1">
                        {diary.diaryImages.map((diaryImage, index) => (
                            <div
                                key={index}
                                className="relative flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onImageClick(diary.diaryImages, index);
                                }}
                            >
                                <img
                                    src={getImageUrl(diaryImage.image, 120, 120)}
                                    alt={`Diary image ${index + 1}`}
                                    className="w-16 h-16 object-cover rounded-lg"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = "/imgs/dummy/basic-img.png";
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 다이어리 메타 정보 영역 */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center space-x-4">
                    <div
                        className={`flex items-center space-x-1 transition-all duration-200 ${isLiking
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer hover:opacity-80'
                            }`}
                        onClick={(e) => !isLiking && onLikeToggle(diary, e)}
                    >
                        {isLiking ? (
                            // 로딩 중일 때 스피너 표시
                            <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            // 일반 좋아요 아이콘
                            diary.isLiked ? (
                                <img src="/imgs/icons/icon-heart-active.svg" alt="like" className="w-4 h-4" />
                            ) : (
                                <img src="/imgs/icons/icon-heart.svg" alt="like" className="w-4 h-4" />
                            )
                        )}
                        <span className={`text-xs font-medium ${diary.isLiked ? 'text-[#CF3688]' : 'text-gray-400'}`}>
                            {diary.likeCount || 0}
                        </span>
                    </div>
                    <div
                        className={`flex items-center space-x-1 cursor-pointer transition-colors text-gray-400`}
                        onClick={(e) => {
                            e.stopPropagation();
                            // 다이어리 상세 팝업 표시
                            showPopup(
                                <StudentDiaryDetailPopup
                                    diaryId={diary.id.toString()}
                                    onClose={() => {
                                        // 팝업이 닫힐 때 다이어리 목록 새로고침
                                        onRefresh();
                                    }}
                                />
                            );
                        }}
                    >
                        <img src="/imgs/icons/icon-comment.svg" alt="comment" className="w-4 h-4" />
                        <span className="text-xs font-medium">
                            {diary.commentCount || 0}
                        </span>
                    </div>
                </div>
                <div className="flex items-center space-x-1">
                    <span 
                        onClick={(e) => {
                            e.stopPropagation();
                            // 다이어리 상세 팝업 표시
                            showPopup(
                                <StudentDiaryDetailPopup
                                    diaryId={diary.id.toString()}
                                    onClose={() => {
                                        // 팝업이 닫힐 때 다이어리 목록 새로고침
                                        onRefresh();
                                    }}
                                />
                            );
                        }} 
                        className="text-xs font-medium text-[#CF3688]"
                    >
                        자세히 보기
                    </span>
                </div>
            </div>
        </div>
    );
};



// 다이어리 스켈레톤 컴포넌트
const DiaryItemSkeleton = () => (
    <div className="p-2 animate-pulse">
        {/* 다이어리 헤더 스켈레톤 */}
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
                <div className="space-y-1">
                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                    <div className="h-2 bg-gray-200 rounded w-12"></div>
                </div>
            </div>
        </div>

        {/* 제목과 내용 스켈레톤 */}
        <div className="mb-2 space-y-1">
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            <div className="h-2 bg-gray-200 rounded w-full"></div>
            <div className="h-2 bg-gray-200 rounded w-4/5"></div>
        </div>

        {/* 이미지 스켈레톤 */}
        <div className="mb-2">
            <div className="flex gap-1">
                <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
            </div>
        </div>

        {/* 메타 정보 스켈레톤 */}
        <div className="flex items-center space-x-4 pt-2 border-t border-gray-100">
            <div className="flex items-center space-x-1">
                <div className="w-4 h-4 bg-gray-200 rounded"></div>
                <div className="h-2 bg-gray-200 rounded w-6"></div>
            </div>
            <div className="flex items-center space-x-1">
                <div className="w-4 h-4 bg-gray-200 rounded"></div>
                <div className="h-2 bg-gray-200 rounded w-6"></div>
            </div>
        </div>
    </div>
);

// 타임라인 스켈레톤 컴포넌트
const TimelineSkeleton = () => (
    <>
        {[1].map((index) => (
            <DiaryItemSkeleton key={index} />
        ))}
    </>
);

const DiaryList: React.FC<DiaryListProps> = ({ userStudentId }) => {
    const { showPopup } = usePopup();
    const [diaryData, setDiaryData] = useState<IDiaryListResult | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isInit, setIsInit] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [likingDiaryIds, setLikingDiaryIds] = useState<Set<number>>(new Set()); // 좋아요 처리 중인 다이어리 ID들

    // 다이어리 목록 조회
    const fetchDiaryList = useCallback(async () => {
        try {
            setIsLoading(true);
            const result = await getDiaryList({ page: 1, userStudentId });

            if (result?.diaryList) {
                setDiaryData(result);
                setHasMore(result.pagination.hasNextPage);
                setCurrentPage(1);
                console.log("📚 초기 다이어리 데이터 로드 완료:", {
                    diaryCount: result.diaryList.length,
                    hasNextPage: result.pagination.hasNextPage,
                    currentPage: 1
                });
            } else {
                setDiaryData({ diaryList: [], pagination: result?.pagination || { hasNextPage: false } as any });
                setHasMore(false);
                console.log("📚 초기 다이어리 데이터 없음");
            }
        } catch (error) {
            console.error("다이어리 목록 조회 실패:", error);
            setDiaryData({ diaryList: [], pagination: { hasNextPage: false } as any });
            setHasMore(false);
        } finally {
            setIsLoading(false);
            setIsInit(true);
        }
    }, [userStudentId]);

    // 추가 데이터 로드 (무한 스크롤용)
    const loadMoreData = useCallback(async () => {
        if (isLoadingMore || !hasMore) {
            console.log("🚫 loadMoreData 호출 차단:", { isLoadingMore, hasMore });
            return;
        }

        try {
            setIsLoadingMore(true);
            const nextPage = currentPage + 1;
            console.log("📄 다음 페이지 로드 시작:", { nextPage, currentPage, hasMore });

            const result = await getDiaryList({ page: nextPage, userStudentId });

            if (result?.diaryList && result.diaryList.length > 0) {
                setDiaryData(prevData => {
                    if (!prevData) return result;

                    const newData = {
                        ...prevData,
                        diaryList: [...prevData.diaryList, ...result.diaryList],
                        pagination: result.pagination
                    };
                    console.log("📊 업데이트된 다이어리 데이터:", {
                        prevCount: prevData.diaryList.length,
                        newCount: result.diaryList.length,
                        totalCount: newData.diaryList.length,
                        hasNextPage: result.pagination.hasNextPage
                    });
                    return newData;
                });
                setCurrentPage(nextPage);
                setHasMore(result.pagination.hasNextPage);
                console.log("✅ 페이지 로드 완료:", { nextPage, hasNextPage: result.pagination.hasNextPage });
            } else {
                console.log("📝 더 이상 로드할 데이터가 없음");
                setHasMore(false);
            }
        } catch (error) {
            console.error("❌ 추가 데이터 로드 실패:", error);
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
        }
    }, [currentPage, hasMore, isLoadingMore, userStudentId]);

    // 좋아요 토글 처리
    const handleLikeToggle = async (diary: IDiary, event: React.MouseEvent) => {
        event.stopPropagation(); // 다이어리 클릭 이벤트 방지

        // 이미 좋아요 처리 중인 다이어리인지 확인
        if (likingDiaryIds.has(diary.id)) {
            console.log("🚫 이미 좋아요 처리 중인 다이어리:", diary.id);
            return;
        }

        try {
            // 좋아요 처리 중인 다이어리 ID 추가
            setLikingDiaryIds(prev => new Set(prev).add(diary.id));
            console.log("❤️ 좋아요 토글 시작:", diary.id);

            const result = await toggleDiaryLike(diary.id.toString());
            console.log("좋아요 토글 결과:", result);

            // 다이어리 목록에서 해당 다이어리의 좋아요 상태와 수 업데이트
            setDiaryData(prevData => {
                if (!prevData) return prevData;

                return {
                    ...prevData,
                    diaryList: prevData.diaryList.map(item =>
                        item.id === diary.id
                            ? {
                                ...item,
                                isLiked: result.isLiked,
                                likeCount: result.likeCount
                            }
                            : item
                    )
                };
            });

        } catch (error) {
            console.error("좋아요 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            // 좋아요 처리 완료 후 ID 제거
            setLikingDiaryIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(diary.id);
                return newSet;
            });
            console.log("✅ 좋아요 토글 완료:", diary.id);
        }
    };

    // 무한 스크롤 이벤트 핸들러
    useEffect(() => {
        const handleScroll = () => {
            // 더 로드할 데이터가 있고 현재 로딩 중이 아닐 때만 다음 페이지 로드
            if (isInit && hasMore && !isLoadingMore) {
                const { scrollTop, clientHeight, scrollHeight } = document.documentElement;
                // 스크롤이 바닥에 도달했는지 체크 (100px 여유를 둠)
                if (scrollHeight - scrollTop <= clientHeight + 100) {
                    console.log("🔄 스크롤 바닥 도달, 다음 페이지 로드 시작");
                    loadMoreData();
                }
            }
        };

        // 스크롤 이벤트 리스너 등록
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isInit, hasMore, isLoadingMore, loadMoreData]);

    // 이미지 클릭 시 팝업 표시
    const handleImageClick = (images: any[], initialIndex: number = 0) => {
        showPopup(
            <ImageSlidePopup
                imageInfoList={images.map(img => img.image)}
                initialSlideIdx={initialIndex}
                onClose={() => { }}
            />
        );
    };

    // 다이어리 클릭 시 상세 화면으로 이동 (필요시 구현)
    const handleDiaryClick = (diaryId: number) => {
        // TODO: 다이어리 상세 팝업 또는 페이지 이동 구현
        console.log("다이어리 클릭:", diaryId);
    };

    // 지망생 정보 팝업 표시
    const handleStudentInfoPopup = (userStudentId: number) => {
        showPopup(
            <StudentProfilePopup
                userStudentId={userStudentId}
            />
        );
    };



    useEffect(() => {
        fetchDiaryList();
    }, [fetchDiaryList]);

    return (
        <>
            {/* 탭 설명 */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-gray-900">다이어리</h3>
                        <p className="text-sm text-gray-600 mt-1">지망생의 일상과 생각을 담은 다이어리를 확인할 수 있습니다. 포트폴리오로 등록된 글도 함께 볼 수 있어요.</p>
                    </div>
                </div>
            </div>
            {isLoading ?
                <>
                    <TimelineSkeleton />
                </>
                :
                <>
                    {diaryData.diaryList.length === 0 &&
                        <div className="text-center py-8">
                            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p className="text-gray-500 text-sm">아직 작성된 다이어리가 없습니다.</p>
                        </div>
                    }
                    <div className="space-y-3 -mx-2">
                        {diaryData.diaryList.map((diary) => {
                            const isLiking = likingDiaryIds.has(diary.id);

                            return (
                                <DiaryItem
                                    key={diary.id}
                                    diary={diary}
                                    isLiking={isLiking}
                                    onLikeToggle={handleLikeToggle}
                                    onCommentClick={(diaryId, e) => {
                                        e.stopPropagation();
                                        showPopup(
                                            <StudentDiaryDetailPopup
                                                diaryId={diaryId.toString()}
                                                onClose={() => {
                                                    fetchDiaryList();
                                                }}
                                            />
                                        );
                                    }}
                                    onStudentInfoPopup={handleStudentInfoPopup}
                                    onImageClick={handleImageClick}
                                    onDiaryClick={handleDiaryClick}
                                    onRefresh={fetchDiaryList}
                                />
                            );
                        })}
                    </div>

                    {/* 추가 로딩 상태 */}
                    {isLoadingMore && (
                        <div className="flex justify-center items-center py-8">
                            <div className="flex items-center space-x-2">
                                <svg className="w-5 h-5 animate-spin text-[#CF3688]" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        </div>
                    )}

                    {/* 더 이상 로드할 데이터가 없을 때 */}
                    {!isLoadingMore && !hasMore && diaryData.diaryList.length > 0 && (
                        <div className="flex justify-center items-center py-8">
                            <div className="text-center">
                                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    )}


                </>
            }
        </>
    );
};

export default DiaryList;
